import React, { useRef, useState } from "react";
import { normalizeHex, letterInkForHex } from "../lib/harmonicPalette";
import {
  isArrowNavKey,
  nextGridBeadIndex,
  nextWrappedLinearIndex,
} from "../lib/arrowFocus";

/**
 * One repeat of the stringing sequence, in the same order as the grouped
 * recipe below it. Each row holds up to `cols` beads laid out right to left
 * so you read top to bottom, right to left.
 */
const BeadString = ({
  runs,
  cols,
  defaultColor,
  letterMap,
  divideAt = null,
  linkedBeadIndexes = null,
  onBeadActivate = null,
}) => {
  const beadsPerRow = Math.max(1, Math.floor(Number(cols)) || 1);
  const rootRef = useRef(null);
  const [activeBeadIndex, setActiveBeadIndex] = useState(0);
  const linkedSet = new Set(
    Array.isArray(linkedBeadIndexes) ? linkedBeadIndexes : []
  );

  const defaultCanon =
    normalizeHex(defaultColor) ||
    String(defaultColor || "")
      .trim()
      .toLowerCase();

  const isEmptyBead = (raw) => {
    if (raw == null || raw === "") return true;
    const canon = normalizeHex(raw) || String(raw).trim().toLowerCase();
    return !canon || canon === defaultCanon;
  };

  const getLetterForHex = (hex) => {
    if (!letterMap) {
      return null;
    }
    const canon = normalizeHex(hex) || String(hex || "").trim().toLowerCase();
    if (canon && letterMap[canon]) return letterMap[canon];
    return letterMap[hex] || null;
  };

  const beads = [];
  (Array.isArray(runs) ? runs : []).forEach((run) => {
    const color = run?.color;
    const count = Math.max(0, Number(run?.count) || 0);
    if (isEmptyBead(color) || count === 0) return;
    for (let i = 0; i < count; i++) {
      beads.push(color);
    }
  });

  if (beads.length === 0) {
    return null;
  }

  const seam =
    Number.isInteger(divideAt) && divideAt > 0 && divideAt < beads.length
      ? divideAt
      : null;

  const chartRows = [];
  for (let i = 0; i < beads.length; ) {
    let take = Math.min(beadsPerRow, beads.length - i);
    if (seam != null && seam > i && seam < i + take) take = seam - i;
    chartRows.push({
      round: chartRows.length + 1,
      divideBefore: seam != null && i === seam,
      beads: beads.slice(i, i + take).map((color, offset) => ({
        color,
        index: i + offset,
      })),
    });
    i += take;
  }

  const lastIndex = beads.length - 1;
  const tabStop = Math.min(Math.max(0, activeBeadIndex), lastIndex);
  const gridRows = chartRows.map((row) => row.beads.map((bead) => bead.index));

  const focusBead = (root, nextIndex) => {
    if (nextIndex < 0) return;
    const target = root.querySelector(`[data-bead-index="${nextIndex}"]`);
    target?.focus();
  };

  const activate = (beadIndex) => {
    setActiveBeadIndex(beadIndex);
    if (typeof onBeadActivate === "function") onBeadActivate(beadIndex);
  };

  const handleBeadKeyDown = (event, beadIndex) => {
    const root = event.currentTarget.closest(".bead-string");
    if (!root) return;
    if (event.key === "Tab") {
      event.preventDefault();
      const nextIndex = nextWrappedLinearIndex(
        beadIndex,
        beads.length,
        event.shiftKey
      );
      focusBead(root, nextIndex);
      return;
    }
    if (!isArrowNavKey(event.key)) return;
    const nextIndex = nextGridBeadIndex(event.key, beadIndex, gridRows);
    if (nextIndex < 0) return;
    event.preventDefault();
    if (nextIndex === beadIndex) return;
    focusBead(root, nextIndex);
  };

  return (
    <div className="bead-string-outer" ref={rootRef}>
      <div className="bead-string-scroll">
        <div className="bead-string-container">
          <ol className="bead-string">
            {chartRows.map(({ round, beads: rowBeads, divideBefore }) => (
              <React.Fragment key={round}>
              {divideBefore && (
                <li className="bead-string__divide">Dissolve</li>
              )}
              <li className="bead-string__row">
                <span className="bead-string__round">{round}</span>
                {rowBeads.map(({ color, index }) => {
                  const letter = getLetterForHex(color);
                  const ink = letterInkForHex(color);
                  const isStart = index === 0;
                  const isEnd = index === lastIndex;
                  const position = index + 1;
                  const isLinked = linkedSet.has(index);

                  const label = `Bead ${position} – ${color}${
                    letter ? ` (${letter})` : ""
                  }`;

                  return (
                    <div
                      key={index}
                      className={`bead-string-item${
                        isStart ? " bead-string-item--start" : ""
                      }${isEnd ? " bead-string-item--end" : ""}${
                        isLinked ? " is-linked" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      title={label}
                      aria-label={label}
                      data-bead-index={index}
                      tabIndex={index === tabStop ? 0 : -1}
                      onClick={(event) => {
                        event.currentTarget.focus();
                      }}
                      onKeyDown={(event) => handleBeadKeyDown(event, index)}
                      onFocus={(event) => {
                        activate(index);
                        event.currentTarget.scrollIntoView?.({
                          block: "nearest",
                          inline: "nearest",
                        });
                      }}
                    >
                      {letter && (
                        <span
                          className="bead-letter"
                          style={{
                            color: ink,
                            textShadow:
                              ink === "#000000"
                                ? "0 0 1px rgba(255, 255, 255, 0.6)"
                                : "1px 1px 1px rgba(0, 0, 0, 0.5)",
                          }}
                        >
                          {letter}
                        </span>
                      )}
                      {isStart && (
                        <span className="bead-string__marker">Start</span>
                      )}
                      {isEnd && (
                        <span className="bead-string__marker">End</span>
                      )}
                    </div>
                  );
                })}
              </li>
              </React.Fragment>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default BeadString;
